import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import Swal from 'sweetalert2';

export default function Tables() {
  const [tables, setTables] = useState([]);
  const [waiters, setWaiters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeZone, setActiveZone] = useState(null); // null = zones view

  // Modals
  const [showZonesModal, setShowZonesModal] = useState(false);
  const [showNewTableModal, setShowNewTableModal] = useState(false);
  const [showOpenModal, setShowOpenModal] = useState(false);
  const [showTableConfigModal, setShowTableConfigModal] = useState(false);
  const [showEditTableModal, setShowEditTableModal] = useState(false);

  const [selectedTable, setSelectedTable] = useState(null);
  const [selectedWaiter, setSelectedWaiter] = useState('');
  const [tableDescription, setTableDescription] = useState('');
  const [tableForm, setTableForm] = useState({ numero: '', zona: '' });
  const [editTableForm, setEditTableForm] = useState({ numero: '', zona: '' });

  // Zone management
  const [newZone, setNewZone] = useState('');
  const [editingZone, setEditingZone] = useState(null); // { original, updated }

  const navigate = useNavigate();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [tablesRes, waitersRes] = await Promise.all([
        api.get('/mesas'),
        api.get('/meseros'),
      ]);
      setTables(tablesRes.data);
      setWaiters(waitersRes.data);
    } catch (error) {
      Swal.fire('Error', 'Could not load data', 'error');
    } finally {
      setLoading(false);
    }
  };

  // ─── Zones ────────────────────────────────────────────────────────────────
  const getZones = () => {
    const zonesSet = new Set();
    [...tables]
      .sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
      .forEach((t) => {
        if (t.zona && t.zona.trim()) zonesSet.add(t.zona.trim());
      });
    return Array.from(zonesSet);
  };

  const getTablesWithoutZone = () => tables.filter((t) => !t.zona || !t.zona.trim());

  const getTablesByZone = (zone) => tables.filter((t) => t.zona && t.zona.trim() === zone);

  const getZoneSummary = (zoneTables) => {
    const available = zoneTables.filter((t) => t.estado === 'disponible').length;
    const occupied = zoneTables.filter((t) => t.estado === 'ocupada').length;
    const pending = zoneTables.filter((t) => t.estado === 'cuenta_pendiente').length;
    return { available, occupied, pending, total: zoneTables.length };
  };

  const handleCreateZone = () => {
    if (!newZone.trim()) return;
    const zones = getZones();
    if (zones.map((z) => z.toLowerCase()).includes(newZone.trim().toLowerCase())) {
      Swal.fire('Notice', 'A zone with that name already exists', 'warning');
      return;
    }
    // The zone is created "empty" — it exists once at least one table is assigned
    setTables((prev) => [...prev, { id: `zone-temp-${Date.now()}`, zona: newZone.trim(), _zoneOnly: true }]);
    setNewZone('');
  };

  const handleDeleteZone = async (zone) => {
    const zoneTables = getTablesByZone(zone);
    if (zoneTables.length > 0) {
      const result = await Swal.fire({
        title: `Delete zone "${zone}"?`,
        text: `It has ${zoneTables.length} table(s). They will become unassigned.`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, delete zone',
        cancelButtonText: 'Cancel',
        confirmButtonColor: '#d33',
      });
      if (!result.isConfirmed) return;
      for (const table of zoneTables) {
        await api.put(`/mesas/${table.id}`, { ...table, zona: '' });
      }
      await loadData();
    } else {
      setTables((prev) => prev.filter((t) => !(t._zoneOnly && t.zona === zone)));
    }
  };

  const handleRenameZone = async (originalZone, newZoneName) => {
    if (!newZoneName.trim() || newZoneName.trim() === originalZone) {
      setEditingZone(null);
      return;
    }
    const zoneTables = getTablesByZone(originalZone);
    try {
      for (const table of zoneTables) {
        await api.put(`/mesas/${table.id}`, { ...table, zona: newZoneName.trim() });
      }
      if (activeZone === originalZone) setActiveZone(newZoneName.trim());
      setEditingZone(null);
      await loadData();
    } catch {
      Swal.fire('Error', 'Could not rename the zone', 'error');
    }
  };

  // ─── Tables ───────────────────────────────────────────────────────────────
  const handleOpenTable = (table) => {
    if (waiters.length === 0) {
      Swal.fire('Notice', 'No waiters registered. Add a waiter first.', 'warning');
      return;
    }
    setSelectedTable(table);
    setSelectedWaiter('');
    setTableDescription('');
    setShowOpenModal(true);
  };

  const handleViewOrder = async (table) => {
    try {
      const res = await api.get(`/ordenes/mesa/${table.id}`);
      navigate(`/ordenes/${res.data.id}`);
    } catch {
      Swal.fire('Error', 'Could not find the active order', 'error');
    }
  };

  const confirmOpenTable = async () => {
    if (!selectedWaiter) {
      Swal.fire('Error', 'Select a waiter', 'error');
      return;
    }
    try {
      const res = await api.post('/ordenes', {
        mesa_id: selectedTable.id,
        mesero_id: selectedWaiter,
        descripcion: tableDescription,
      });
      setShowOpenModal(false);
      navigate(`/ordenes/${res.data.id}`);
    } catch {
      Swal.fire('Error', 'Could not open the table', 'error');
    }
  };

  const handleSaveNewTable = async () => {
    if (!tableForm.numero) {
      Swal.fire('Error', 'Table number is required', 'error');
      return;
    }
    try {
      await api.post('/mesas', { ...tableForm, zona: activeZone || tableForm.zona || '' });
      Swal.fire('Success', 'Table created successfully', 'success');
      setShowNewTableModal(false);
      setTableForm({ numero: '', zona: '' });
      loadData();
    } catch {
      Swal.fire('Error', 'Could not create the table', 'error');
    }
  };

  const handleEditTable = async () => {
    if (!editTableForm.numero) {
      Swal.fire('Error', 'Table number is required', 'error');
      return;
    }
    try {
      await api.put(`/mesas/${selectedTable.id}`, editTableForm);
      Swal.fire('Success', 'Table updated', 'success');
      setShowEditTableModal(false);
      setShowTableConfigModal(false);
      loadData();
    } catch {
      Swal.fire('Error', 'Could not update the table', 'error');
    }
  };

  const handleDeleteTable = async (id) => {
    const result = await Swal.fire({
      title: 'Delete table?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#d33',
    });
    if (result.isConfirmed) {
      try {
        await api.delete(`/mesas/${id}`);
        Swal.fire('Success', 'Table deleted successfully', 'success');
        setShowTableConfigModal(false);
        loadData();
      } catch {
        Swal.fire('Error', 'Could not delete the table', 'error');
      }
    }
  };

  // ─── Visual helpers ─────────────────────────────────────────────────────
  const getStatusColor = (status) => {
    switch (status) {
      case 'disponible':
        return '#28a745';
      case 'ocupada':
        return '#dc3545';
      case 'cuenta_pendiente':
        return '#ffc107';
      default:
        return '#6c757d';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'disponible':
        return 'AVAILABLE';
      case 'ocupada':
        return 'OCCUPIED';
      case 'cuenta_pendiente':
        return 'CHECK PENDING';
      default:
        return status;
    }
  };

  const getZoneIcon = (zone) => {
    const z = zone.toLowerCase();
    if (z.includes('sala')) return '🪑';
    if (z.includes('patio') || z.includes('exterior') || z.includes('terraza')) return '🌿';
    if (z.includes('planta') || z.includes('piso')) return '🏢';
    if (z.includes('vip') || z.includes('privado')) return '⭐';
    if (z.includes('familiar') || z.includes('familia')) return '👨‍👩‍👧';
    if (z.includes('bar')) return '🍹';
    return '📍';
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '60vh' }}>
        <div className="spinner-border text-primary" role="status" />
      </div>
    );
  }

  const zones = getZones();
  const tablesWithoutZone = getTablesWithoutZone().filter((t) => !t._zoneOnly);

  // ─── ZONE DETAIL VIEW ───────────────────────────────────────────────
  if (activeZone !== null) {
    const isNoZone = activeZone === '__sin_zona__';
    const zoneTables = isNoZone ? tablesWithoutZone : getTablesByZone(activeZone).filter((t) => !t._zoneOnly);
    const zoneName = isNoZone ? 'Unassigned' : activeZone;

    return (
      <div className="p-4">
        {/* Header */}
        <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <button className="btn btn-outline-secondary btn-sm" onClick={() => setActiveZone(null)}>
              ← Back
            </button>
            <h5 className="mb-0">
              {!isNoZone && <span className="me-1">{getZoneIcon(activeZone)}</span>}
              {zoneName}
            </h5>
            <span className="badge bg-secondary">{zoneTables.length} tables</span>
          </div>
          <button
            className="btn btn-primary btn-sm"
            onClick={() => {
              setTableForm({ numero: '', zona: isNoZone ? '' : activeZone });
              setShowNewTableModal(true);
            }}
          >
            + New Table
          </button>
        </div>

        {/* Zone tables */}
        {zoneTables.length === 0 ? (
          <div className="text-center text-muted mt-5">
            <div style={{ fontSize: 48 }}>🍽️</div>
            <h5 className="mt-3">No tables in this zone</h5>
            <p>Add the first table with the "New Table" button</p>
          </div>
        ) : (
          <div className="row g-3">
            {zoneTables.map((table) => (
              <div key={table.id} className="col-6 col-md-4 col-lg-3">
                <div
                  className="card h-100 text-center"
                  style={{
                    borderTop: `4px solid ${getStatusColor(table.estado)}`,
                    borderRadius: '12px',
                    position: 'relative',
                  }}
                >
                  <button
                    style={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      background: 'none',
                      border: 'none',
                      fontSize: '18px',
                      cursor: 'pointer',
                      color: '#6c757d',
                      padding: '2px 6px',
                      borderRadius: '50%',
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedTable(table);
                      setEditTableForm({ numero: table.numero, zona: table.zona || '' });
                      setShowTableConfigModal(true);
                    }}
                    title="Configure table"
                  >
                    ⚙️
                  </button>
                  <div className="card-body">
                    <h3 className="card-title fw-bold">Table {table.numero}</h3>
                    <span className="badge mb-3" style={{ backgroundColor: getStatusColor(table.estado) }}>
                      {getStatusText(table.estado)}
                    </span>
                    <div className="d-flex flex-column gap-2 align-items-center">
                      {table.estado === 'ocupada' || table.estado === 'cuenta_pendiente' ? (
                        <button className="btn btn-sm btn-warning" onClick={() => handleViewOrder(table)}>
                          👁 View Order
                        </button>
                      ) : (
                        <button className="btn btn-sm btn-success" onClick={() => handleOpenTable(table)}>
                          Open
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Table Config Modal */}
        {showTableConfigModal && selectedTable && (
          <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-sm">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">⚙️ Table {selectedTable.numero}</h5>
                  <button className="btn-close" onClick={() => setShowTableConfigModal(false)} />
                </div>
                <div className="modal-body d-flex flex-column gap-2">
                  <button
                    className="btn btn-outline-primary w-100"
                    onClick={() => {
                      setShowTableConfigModal(false);
                      setShowEditTableModal(true);
                    }}
                  >
                    ✏️ Edit Table
                  </button>
                  <button className="btn btn-danger w-100" onClick={() => handleDeleteTable(selectedTable.id)}>
                    🗑️ Delete Table
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Edit Table Modal */}
        {showEditTableModal && selectedTable && (
          <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">✏️ Edit Table {selectedTable.numero}</h5>
                  <button className="btn-close" onClick={() => setShowEditTableModal(false)} />
                </div>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label">Table Number *</label>
                    <input
                      type="text"
                      className="form-control"
                      value={editTableForm.numero}
                      onChange={(e) => setEditTableForm({ ...editTableForm, numero: e.target.value })}
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Zone</label>
                    <select
                      className="form-select"
                      value={editTableForm.zona}
                      onChange={(e) => setEditTableForm({ ...editTableForm, zona: e.target.value })}
                    >
                      <option value="">No zone</option>
                      {zones.map((z) => (
                        <option key={z} value={z}>
                          {z}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="modal-footer">
                  <button className="btn btn-secondary" onClick={() => setShowEditTableModal(false)}>
                    Cancel
                  </button>
                  <button className="btn btn-primary" onClick={handleEditTable}>
                    Save
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Open Table Modal */}
        {showOpenModal && (
          <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Open Table {selectedTable?.numero}</h5>
                  <button className="btn-close" onClick={() => setShowOpenModal(false)} />
                </div>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label">Select waiter *</label>
                    <select
                      className="form-select"
                      value={selectedWaiter}
                      onChange={(e) => setSelectedWaiter(e.target.value)}
                    >
                      <option value="">-- Select waiter --</option>
                      {waiters.map((w) => (
                        <option key={w.id} value={w.id}>
                          {w.nombre}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Description (optional)</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="E.g.: Raúl's table"
                      value={tableDescription}
                      onChange={(e) => setTableDescription(e.target.value)}
                    />
                  </div>
                </div>
                <div className="modal-footer">
                  <button className="btn btn-secondary" onClick={() => setShowOpenModal(false)}>
                    Cancel
                  </button>
                  <button className="btn btn-success" onClick={confirmOpenTable}>
                    Open Table
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* New Table Modal */}
        {showNewTableModal && (
          <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">New Table in {zoneName}</h5>
                  <button className="btn-close" onClick={() => setShowNewTableModal(false)} />
                </div>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label">Table Number *</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="E.g.: 1, 2, VIP1"
                      value={tableForm.numero}
                      onChange={(e) => setTableForm({ ...tableForm, numero: e.target.value })}
                    />
                  </div>
                  {isNoZone && (
                    <div className="mb-3">
                      <label className="form-label">Zone (optional)</label>
                      <select
                        className="form-select"
                        value={tableForm.zona}
                        onChange={(e) => setTableForm({ ...tableForm, zona: e.target.value })}
                      >
                        <option value="">No zone</option>
                        {zones.map((z) => (
                          <option key={z} value={z}>
                            {z}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
                <div className="modal-footer">
                  <button className="btn btn-secondary" onClick={() => setShowNewTableModal(false)}>
                    Cancel
                  </button>
                  <button className="btn btn-primary" onClick={handleSaveNewTable}>
                    Save
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ─── MAIN ZONES VIEW ─────────────────────────────────────────────
  return (
    <div className="p-4">
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
        <h2 className="mb-0">Tables</h2>
        <div className="d-flex gap-2 flex-wrap">
          <button className="btn btn-outline-secondary btn-sm" onClick={() => setShowZonesModal(true)}>
            🗂️ Manage Zones
          </button>
          <button
            className="btn btn-primary btn-sm"
            onClick={() => {
              setTableForm({ numero: '', zona: zones[0] || '' });
              setShowNewTableModal(true);
            }}
          >
            + New Table
          </button>
        </div>
      </div>

      {zones.length === 0 && tablesWithoutZone.length === 0 ? (
        <div className="text-center text-muted mt-5">
          <div style={{ fontSize: 56 }}>🍽️</div>
          <h5 className="mt-3">No zones or tables registered</h5>
          <p>Start by creating a zone with the "Manage Zones" button</p>
        </div>
      ) : (
        <div className="row g-4">
          {/* Zone cards */}
          {[...zones, ...tables.filter((t) => t._zoneOnly).map((t) => t.zona)]
            .filter((v, i, a) => a.indexOf(v) === i)
            .map((zone) => {
              const zoneTables = getTablesByZone(zone).filter((t) => !t._zoneOnly);
              const { available, occupied, pending, total } = getZoneSummary(zoneTables);
              const hasOccupied = occupied > 0 || pending > 0;

              return (
                <div key={zone} className="col-12 col-md-6 col-lg-4">
                  <div
                    className="card h-100"
                    style={{
                      borderRadius: '16px',
                      border: '1px solid #e0e0e0',
                      cursor: 'pointer',
                      transition: 'transform 0.15s, box-shadow 0.15s',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                    }}
                    onClick={() => setActiveZone(zone)}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-3px)';
                      e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.12)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.06)';
                    }}
                  >
                    <div className="card-body p-4">
                      <div className="d-flex justify-content-between align-items-start mb-3">
                        <div>
                          <span style={{ fontSize: 32 }}>{getZoneIcon(zone)}</span>
                          <h4 className="fw-bold mt-1 mb-0">{zone}</h4>
                          <small className="text-muted">
                            {total} table{total !== 1 ? 's' : ''}
                          </small>
                        </div>
                        {hasOccupied && (
                          <span className="badge bg-danger" style={{ fontSize: '0.75rem' }}>
                            {occupied + pending} occupied
                          </span>
                        )}
                      </div>

                      {total > 0 && (
                        <div className="mb-3">
                          <div className="progress" style={{ height: 8, borderRadius: 8 }}>
                            <div className="progress-bar bg-success" style={{ width: `${(available / total) * 100}%` }} />
                            <div className="progress-bar bg-danger" style={{ width: `${(occupied / total) * 100}%` }} />
                            <div className="progress-bar bg-warning" style={{ width: `${(pending / total) * 100}%` }} />
                          </div>
                        </div>
                      )}

                      <div className="d-flex gap-3">
                        <span className="text-success fw-semibold">
                          ✔ {available} free
                        </span>
                        {occupied > 0 && (
                          <span className="text-danger fw-semibold">
                            ● {occupied} occupied
                          </span>
                        )}
                        {pending > 0 && (
                          <span className="text-warning fw-semibold">
                            ⏳ {pending} pending
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}

          {/* Unassigned tables card */}
          {tablesWithoutZone.length > 0 && (
            <div className="col-12 col-md-6 col-lg-4">
              <div
                className="card h-100"
                style={{
                  borderRadius: '16px',
                  border: '1px dashed #aaa',
                  cursor: 'pointer',
                  transition: 'transform 0.15s, box-shadow 0.15s',
                  backgroundColor: '#fafafa',
                }}
                onClick={() => setActiveZone('__sin_zona__')}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-3px)';
                  e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.08)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <div className="card-body p-4">
                  <span style={{ fontSize: 32 }}>📦</span>
                  <h4 className="fw-bold mt-1 mb-0">Unassigned</h4>
                  <small className="text-muted">
                    {tablesWithoutZone.length} table{tablesWithoutZone.length !== 1 ? 's' : ''} unassigned
                  </small>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Manage Zones Modal */}
      {showZonesModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">🗂️ Manage Zones</h5>
                <button
                  className="btn-close"
                  onClick={() => {
                    setShowZonesModal(false);
                    setEditingZone(null);
                    setNewZone('');
                  }}
                />
              </div>
              <div className="modal-body">
                {/* Create new zone */}
                <div className="mb-4">
                  <label className="form-label fw-semibold">New zone</label>
                  <div className="input-group">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="E.g.: Patio, Bar, VIP..."
                      value={newZone}
                      onChange={(e) => setNewZone(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleCreateZone()}
                    />
                    <button className="btn btn-primary" onClick={handleCreateZone}>
                      Add
                    </button>
                  </div>
                </div>

                {/* Zone list */}
                <label className="form-label fw-semibold">Existing zones</label>
                {[...zones, ...tables.filter((t) => t._zoneOnly).map((t) => t.zona)].filter((v, i, a) => a.indexOf(v) === i)
                  .length === 0 ? (
                  <p className="text-muted">No zones yet. Add the first one above.</p>
                ) : (
                  <ul className="list-group">
                    {[...zones, ...tables.filter((t) => t._zoneOnly).map((t) => t.zona)]
                      .filter((v, i, a) => a.indexOf(v) === i)
                      .map((zone) => {
                        const count = getTablesByZone(zone).filter((t) => !t._zoneOnly).length;
                        const isEditing = editingZone?.original === zone;
                        return (
                          <li key={zone} className="list-group-item d-flex align-items-center gap-2">
                            <span className="me-1">{getZoneIcon(zone)}</span>
                            {isEditing ? (
                              <input
                                type="text"
                                className="form-control form-control-sm"
                                value={editingZone.updated}
                                autoFocus
                                onChange={(e) => setEditingZone({ ...editingZone, updated: e.target.value })}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') handleRenameZone(zone, editingZone.updated);
                                  if (e.key === 'Escape') setEditingZone(null);
                                }}
                              />
                            ) : (
                              <span className="flex-grow-1">
                                {zone} <small className="text-muted">({count} table{count !== 1 ? 's' : ''})</small>
                              </span>
                            )}
                            <div className="d-flex gap-1 ms-auto">
                              {isEditing ? (
                                <>
                                  <button
                                    className="btn btn-sm btn-success"
                                    onClick={() => handleRenameZone(zone, editingZone.updated)}
                                  >
                                    ✔
                                  </button>
                                  <button className="btn btn-sm btn-secondary" onClick={() => setEditingZone(null)}>
                                    ✕
                                  </button>
                                </>
                              ) : (
                                <>
                                  <button
                                    className="btn btn-sm btn-outline-secondary"
                                    title="Rename"
                                    onClick={() => setEditingZone({ original: zone, updated: zone })}
                                  >
                                    ✏️
                                  </button>
                                  <button
                                    className="btn btn-sm btn-outline-danger"
                                    title="Delete zone"
                                    onClick={() => handleDeleteZone(zone)}
                                  >
                                    🗑️
                                  </button>
                                </>
                              )}
                            </div>
                          </li>
                        );
                      })}
                  </ul>
                )}
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowZonesModal(false);
                    setEditingZone(null);
                    setNewZone('');
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* New Table Modal (from main view) */}
      {showNewTableModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">New Table</h5>
                <button className="btn-close" onClick={() => setShowNewTableModal(false)} />
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">Table Number *</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="E.g.: 1, 2, VIP1"
                    value={tableForm.numero}
                    onChange={(e) => setTableForm({ ...tableForm, numero: e.target.value })}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Zone</label>
                  <select
                    className="form-select"
                    value={tableForm.zona}
                    onChange={(e) => setTableForm({ ...tableForm, zona: e.target.value })}
                  >
                    <option value="">No zone</option>
                    {zones.map((z) => (
                      <option key={z} value={z}>
                        {z}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowNewTableModal(false)}>
                  Cancel
                </button>
                <button className="btn btn-primary" onClick={handleSaveNewTable}>
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}